import { useState } from 'react';
import { useIntl } from 'react-intl';

import { CoverageBadge } from '../../CoverageBadge';
import { CoverageAvailableInBadge } from '../../CoverageAvailableInBadge';
import { BudgetCurrencyInput } from '../../budgetInputs/BudgetCurrencyInput';
import { BudgetPercentageInput } from '../../budgetInputs/BudgetPercentageInput';
import { BudgetQuantityInput } from '../../budgetInputs/BudgetQuantityInput';

import { BudgetItemSelect } from './BudgetItemSelect';
import { BudgetDescriptionSelect } from './BudgetDescriptionSelect';
import { mapCatalogItemToVersionServiceItem } from './BudgetServiceChooser';
import { findInitialServiceItem } from '../utils/findInitialServiceItem';

import { IBudgetServiceItem, IBudgetVersionService, IBudgetVersionServiceItem } from '../../../../../models/budgetV2';

import { formatNumberToCurrency, stringToNumber } from '../../../../../utils/stringUtils';

import ConfirmDialog from '../../../../dialogs/ConfirmDialog';
import ErrorDialog from '../../../../dialogs/ErrorDialog';

interface IBudgetServiceItemProps {
  service: IBudgetVersionService;
  serviceItem: IBudgetVersionServiceItem;
  initialServiceItems?: IBudgetVersionServiceItem[];
  showDiscountColumn?: boolean;
  loadingServiceItems?: boolean;

  setServiceItem: (item: IBudgetVersionServiceItem) => void;
  removeServiceItem: () => void;
  isItemInService?: (itemCode: string) => boolean;
  isCreating?: boolean;
}

const SkeletonBlock = ({ className = "" }: { className?: string }) => (
  <div className={`animate-pulse rounded bg-gray-300 ${className}`} />
);

export const BudgetServiceItem = (props: IBudgetServiceItemProps) => {
  const intl = useIntl();

  const { service, serviceItem, initialServiceItems, showDiscountColumn, loadingServiceItems, setServiceItem, removeServiceItem, isItemInService, isCreating } = props;

  // Snapshot taken when the budget was loaded; items added/picked afterwards have no original.
  const originalServiceItem = findInitialServiceItem(initialServiceItems, serviceItem);

  if (!serviceItem) return null;

  // On edit, catalog fields of an existing item are locked: to change them the user removes the item and adds another.
  const isExistingItem = !isCreating && originalServiceItem != null;
  const lockCatalogFields = serviceItem.removed || !!serviceItem.isCovered || isExistingItem;

  const quantityChanged = !isCreating && !serviceItem.removed && originalServiceItem != null && serviceItem.quantity !== originalServiceItem.quantity;
  const valueChanged = !isCreating && !serviceItem.removed && originalServiceItem != null && serviceItem.value !== originalServiceItem.value;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  const fillFromCatalogItem = (item: IBudgetServiceItem) => {
    if (item.item && item.item !== serviceItem.item && isItemInService?.(item.item)) {
      setShowDuplicateDialog(true);
      return;
    }
    const { id, ref, ...catalogFields } = mapCatalogItemToVersionServiceItem(item);
    setServiceItem({ ...serviceItem, ...catalogFields, quantity: catalogFields.quantity || 1, changed: true });
  };

  const restoreItem = () => {
    const differsFromOriginal = originalServiceItem != null
      && (serviceItem.quantity !== originalServiceItem.quantity || serviceItem.value !== originalServiceItem.value);
    setServiceItem({ ...serviceItem, removed: false, changed: differsFromOriginal });
  };

  return (
    <>
      <tr
        className={`border-0 !border-b-0 ${serviceItem.removed ? 'h-[84px]' : 'h-[50px]'}`}
        style={{ borderBottom: 'none' }}
      >
        <td className="border-0 !border-b-0 " style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0' }}>
          {loadingServiceItems ? (<SkeletonBlock className="h-[20px] w-full" />) : (serviceItem.ref)}
        </td>
        <td className="border-0 !border-b-0 relative" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0', overflow: 'visible' }}>
          {serviceItem.removed && (
            <div className="text-red-400 text-size-14 font-bold px-2 absolute bottom-[-0px] left-0 pl-5 whitespace-nowrap z-10">
              {loadingServiceItems ? <SkeletonBlock className="h-[20px] w-full" /> : intl.formatMessage({ id: 'intl-msg:budgets.removed' })}
            </div>
          )}
          {service.isExternalService ? (
            loadingServiceItems ? (
              <SkeletonBlock className="h-[20px] w-full" />
            ) : (
              <input
                type="text"
                className={`form-control ${serviceItem.removed ? '!bg-red-50' : ''}`}
                value={serviceItem.item || ''}
                onChange={(e) => {
                  if (!serviceItem.removed && serviceItem.quantity !== 0) {
                    setServiceItem({ ...serviceItem, item: e.target.value, changed: true });
                  }
                }}
                disabled={lockCatalogFields}
                style={serviceItem.removed ? { backgroundColor: '#fef2f2' } : undefined}
                autoFocus={(!serviceItem.item || serviceItem.item.trim() === '') && !serviceItem.removed}
              />
            )
          ) : (
            loadingServiceItems ? (
              <SkeletonBlock className="h-[20px] w-full" />
            ) : (
              <BudgetItemSelect
                value={serviceItem.item || ''}
                onChange={(item) => {
                  if (!serviceItem.removed && serviceItem.quantity !== 0) {
                    if (item) {
                      fillFromCatalogItem(item);
                    } else {
                      setServiceItem({ ...serviceItem, item: '', changed: true });
                    }
                  }
                }}
                disabled={lockCatalogFields}
                isRemoved={serviceItem.removed}
                autoFocus={(!serviceItem.item || serviceItem.item.trim() === '') && !serviceItem.removed}
              />
            ))}
        </td>
        <td className="border-0 !border-b-0 relative" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0', overflow: 'visible' }}>
          {service.isExternalService ? (
            loadingServiceItems ? (
              <SkeletonBlock className="h-[20px] w-full" />
            ) : (
              <input
                type="text"
                className={`form-control ${serviceItem.removed ? '!bg-red-50' : ''}`}
                value={serviceItem.description || ''}
                onChange={(e) => {
                  if (!serviceItem.removed && serviceItem.quantity !== 0) {
                    setServiceItem({ ...serviceItem, description: e.target.value, changed: true });
                  }
                }}
                disabled={lockCatalogFields}
                style={serviceItem.removed ? { backgroundColor: '#fef2f2' } : undefined}
                placeholder={intl.formatMessage({ id: 'intl-msg:budgets.description' })}
              />)
          ) : (
            loadingServiceItems ? (
              <SkeletonBlock className="h-[20px] w-full" />
            ) : (
              <BudgetDescriptionSelect
                value={serviceItem.description || ''}
                onChange={(item) => {
                  if (!serviceItem.removed && serviceItem.quantity !== 0) {
                    if (item) {
                      fillFromCatalogItem(item);
                    } else {
                      setServiceItem({ ...serviceItem, description: '', changed: true });
                    }
                  }
                }}
                disabled={lockCatalogFields}
                isRemoved={serviceItem.removed}
                autoFocus={false}
              />)
          )}
        </td>
        <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0' }}>
          <div className="relative">
            {loadingServiceItems ? (
              <SkeletonBlock className="h-[20px] w-full" />
            ) : (
              <BudgetQuantityInput
                value={serviceItem.removed && serviceItem.quantity ? serviceItem.quantity : serviceItem.quantity || ''}
                onChange={(value) => {
                  if (!serviceItem.removed && serviceItem.quantity !== 0) {
                    setServiceItem({ ...serviceItem, quantity: stringToNumber(value), changed: true });
                  }
                }}
                disabled={serviceItem.removed || !!serviceItem.isCovered}
                isRemoved={serviceItem.removed}
                isChanged={quantityChanged}
              />)}
            {quantityChanged && !!serviceItem.quantity && (
              <span className="absolute font-bold text-size-12 text-gray-500 top-full left-0 mt-0.5 whitespace-nowrap">{intl.formatMessage({ id: 'intl-msg:budgets.previous' })} ({originalServiceItem.quantity})</span>
            )}
          </div>
        </td>
        <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0' }}>
          <div className="relative">
            {loadingServiceItems ? (
              <SkeletonBlock className="h-[20px] w-full" />
            ) : (
              <BudgetCurrencyInput
                value={serviceItem.value || 0}
                onChange={(value) => {
                  if (!serviceItem.removed && serviceItem.quantity !== 0) {
                    setServiceItem({ ...serviceItem, value: value, changed: true });
                  }
                }}
                disabled={serviceItem.removed || !!serviceItem.isCovered}
                isRemoved={serviceItem.removed}
                isChanged={valueChanged}
              />)}
            {valueChanged && originalServiceItem.value !== undefined && (
              <span className="absolute font-bold text-size-12 text-gray-500 top-full left-0 mt-0.5 whitespace-nowrap">{intl.formatMessage({ id: 'intl-msg:budgets.previous' })} ({formatNumberToCurrency(originalServiceItem.value)})</span>
            )}
          </div>
        </td>
        <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0' }}>
          {loadingServiceItems ? (
            <SkeletonBlock className="h-[20px] w-full" />
          ) : (
            <CoverageBadge isCovered={serviceItem.isCovered === true} notCoveredReason={serviceItem.notCoveredReason} availableIn={serviceItem.availableIn} />
          )}
        </td>
        <td className="border-0 !border-b-0 text-center" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0' }}>
          {loadingServiceItems ? (
            <SkeletonBlock className="h-[20px] w-full" />
          ) : (
            <CoverageAvailableInBadge isCovered={serviceItem.isCovered === true} availableIn={serviceItem.availableIn} />
          )}
        </td>
        {showDiscountColumn && (
          <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0' }}>
            {loadingServiceItems ? (
              <SkeletonBlock className="h-[20px] w-full" />
            ) : (
              <BudgetPercentageInput
                value={serviceItem.discount || 0}
                onChange={(value: any) => {
                  if (!serviceItem.removed && serviceItem.quantity !== 0 && !!!serviceItem.isCovered) {
                    const discountValue =
                      value % 1 === 0
                        ? `${String(value).padStart(2, '0')}%`
                        : `${value.toFixed(2).replace('.', ',')}%`;
                    setServiceItem({ ...serviceItem, discount: stringToNumber(discountValue), changed: true });
                  }
                }}
                disabled={serviceItem.removed || !!serviceItem.isCovered}
                isRemoved={serviceItem.removed}
              />)}
          </td>
        )}
        <td className="border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0' }}>
          {loadingServiceItems ? (
            <SkeletonBlock className="h-[20px] w-full" />
          ) : (
            <BudgetCurrencyInput
              value={(serviceItem.quantity || 0) * (serviceItem.value || 0) * (1 - (serviceItem.discount || 0) / 100)}
              onChange={(value: any) => setServiceItem({ ...serviceItem, subtotal: stringToNumber(value), changed: true })}
              disabled={true}
              isRemoved={serviceItem.removed}
            />)}
        </td>
        <td className="text-center border-0 !border-b-0" style={{ borderBottom: 'none', paddingBottom: serviceItem.changed ? '25px' : '0' }}>
          {loadingServiceItems ? (
            <SkeletonBlock className="h-[20px] w-full" />
          ) : (
            !serviceItem.isCovered && (serviceItem.removed ? (
              <button
                type="button"
                className="cursor-pointer bg-transparent border-none p-0 text-center"
                onClick={restoreItem}
                title={intl.formatMessage({ id: 'intl-msg:budgets.restoreItem' })}
                aria-label={intl.formatMessage({ id: 'intl-msg:budgets.restoreItem' })}
              >
                <svg className="w-6 h-6 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 7v6h6" />
                  <path d="M21 17a9 9 0 0 0-15-6.7L3 13" />
                </svg>
              </button>
            ) : (
              <button
                type="button"
                className="cursor-pointer bg-transparent border-none p-0 text-center"
                onClick={() => {
                  if (serviceItem.quantity !== 0) {
                    setShowConfirmDialog(true);
                  }
                }}
              >
                <img src="/assets/images/bin.svg" alt={intl.formatMessage({ id: 'intl-msg:budgets.removeItem' })} className="w-6 h-6" />
              </button>
            )))}
        </td>
      </tr>

      <ErrorDialog
        show={showDuplicateDialog}
        onClose={() => setShowDuplicateDialog(false)}
        title={intl.formatMessage({ id: 'intl-msg:budgets.duplicateItem.title' })}
        body={intl.formatMessage({ id: 'intl-msg:budgets.duplicateItem.body' })}
      />

      <ConfirmDialog
        show={showConfirmDialog}
        title={intl.formatMessage({ id: 'intl-msg:budgets.removeItem' })}
        body={intl.formatMessage({ id: 'intl-msg:budgets.removeItemConfirmation' })}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          removeServiceItem();
          setShowConfirmDialog(false);
        }}
      />
    </>
  );
};
